import React, { useEffect, useMemo, useState } from 'react';
import { Check, CreditCard, Gift, WalletCards, X } from 'lucide-react';

export type SendRecipient = {
    name: string;
    groupKey: string;
    phone?: string;
    reason?: string;
    trainer?: string;
    detail?: string;
    /** 서버 발송용 식별자 — 회원 수신자 */
    memberId?: string;
    /** 서버 발송용 식별자 — 강사 수신자 */
    trainerId?: string;
};

export type SendProduct = {
    id: string;
    brand: string;
    name: string;
    price: number;
};

export type SendGiftGroup = {
    key: string;
    label: string;
    product: SendProduct;
};

export type SendConfirmPayload = {
    recipients: SendRecipient[];
    giftGroups: SendGiftGroup[];
    payment: '충전 잔액' | '바로 결제';
    message: string;
    reason: string;
    route: '직접 발송' | '대시보드 추천' | '자동 규칙' | '단체 캠페인' | '강사 발송';
    recipientType: '회원' | '강사';
};

export type DirectPayMethod = 'BILLING' | 'CHECKOUT';

export type RegisteredCard = {
    hasBillingCard: boolean;
    cardName: string;
    cardNumberMasked: string;
    directPayConsented: boolean;
};

export type SendConfirmResult = {
    recipients: SendRecipient[];
    productLabel: string;
    totalAmount: number;
    payment: '충전 잔액' | '바로 결제';
    message: string;
    reason: string;
    route: SendConfirmPayload['route'];
    receiverType: '회원' | '강사' | '단체';
    /** 바로 결제는 토스 일반결제 결제창(CHECKOUT)만 지원 — 빌링키 결제는 백엔드 미구현(Phase 3b) */
    directPayMethod?: DirectPayMethod;
    /** 이 모달 인스턴스의 재시도 전체에서 동일한 멱등키 베이스 — 그룹별로 `:productId` 를 붙여 사용 */
    idempotencyBaseKey: string;
};

const point = (value: number) => `${value.toLocaleString()}P`;
const money = (value: number) => `${value.toLocaleString()}원`;

const messageTemplates = [
    { label: '생일 축하', value: '{이름}님, 생일을 진심으로 축하드려요! 오늘 하루 행복하게 보내세요.' },
    { label: '재등록 감사', value: '{이름}님, 다시 함께해 주셔서 감사합니다. 앞으로도 즐겁게 운동해요!' },
    { label: '응원', value: '{이름}님, 늘 응원하고 있어요. 작은 선물과 함께 힘찬 하루 보내세요!' },
];

const SendConfirmModal = ({
    payload,
    pointBalance,
    onConfirm,
    onRequestProductChange,
    onRequestCharge,
    onClose,
    onSent,
}: {
    payload: SendConfirmPayload;
    pointBalance: number;
    /** 발송(또는 결제창 이동)을 서버 확정까지 수행. 실패 시 reject — 모달이 에러를 표시한다.
     *  resolve 값 { queued: true } = 대량 건이라 서버가 백그라운드 발급을 시작함 (성공 문구 분기용) */
    onConfirm: (result: SendConfirmResult) => Promise<void | { queued?: boolean }>;
    onRequestProductChange: (groupKey?: string) => void;
    onRequestCharge: () => void;
    onClose: () => void;
    /** 발송 성공 화면에서 닫을 때 — 페이지가 선택 초기화·내역 이동을 수행 */
    onSent?: () => void;
}) => {
    const [isSent, setIsSent] = useState(false);
    const [isQueuedSend, setIsQueuedSend] = useState(false);
    // 모달이 열려 있는 동안(재시도 포함) 동일한 멱등키를 유지 — 타임아웃 후 재클릭이 이중 발송이 되지 않게 한다
    const [idempotencyBaseKey] = useState(() => (
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `gift-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    ));
    const [recipients, setRecipients] = useState(payload.recipients);
    const [giftGroups, setGiftGroups] = useState(payload.giftGroups);
    const [message, setMessage] = useState(payload.message);
    const [payment, setPayment] = useState<'충전 잔액' | '바로 결제'>(payload.payment);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        setGiftGroups(payload.giftGroups);
    }, [payload.giftGroups]);

    const groupRecipientCount = (groupKey: string) => recipients.filter((recipient) => recipient.groupKey === groupKey).length;
    const visibleGiftGroups = giftGroups.filter((group) => groupRecipientCount(group.key) > 0);
    const pointTotal = visibleGiftGroups.reduce((sum, group) => sum + group.product.price * groupRecipientCount(group.key), 0);
    const directTotal = visibleGiftGroups.reduce((sum, group) => sum + Math.round(group.product.price * 0.97) * groupRecipientCount(group.key), 0);
    const directDiscount = pointTotal - directTotal;
    const totalAmount = payment === '충전 잔액' ? pointTotal : directTotal;
    const hasEnoughPoints = pointBalance >= pointTotal;
    const firstName = recipients[0]?.name ?? '회원';
    const previewMessage = message.replaceAll('{이름}', firstName);
    const previewGroup = giftGroups.find((group) => group.key === recipients[0]?.groupKey) ?? giftGroups[0];
    const productSummary = visibleGiftGroups.length > 1
        ? `${visibleGiftGroups[0].product.brand} ${visibleGiftGroups[0].product.name} 외 ${visibleGiftGroups.length - 1}종`
        : visibleGiftGroups[0]
            ? `${visibleGiftGroups[0].product.brand} ${visibleGiftGroups[0].product.name}`
            : '선물';
    const formatAmount = payment === '충전 잔액' ? point : money;
    // 바로 결제(토스 결제창)는 결제 1건 = 배치 1건이라 단일 상품 발송에서만 지원
    const checkoutBlockedByMultiGroup = payment === '바로 결제' && visibleGiftGroups.length > 1;
    const canSend = recipients.length > 0 && message.trim().length > 0
        && (payment === '충전 잔액' ? hasEnoughPoints : !checkoutBlockedByMultiGroup);

    const reasonCounts = useMemo(() => {
        const counts = new Map<string, number>();
        recipients.forEach((recipient) => {
            const reason = recipient.reason ?? payload.reason;
            counts.set(reason, (counts.get(reason) ?? 0) + 1);
        });
        return Array.from(counts.entries());
    }, [payload.reason, recipients]);

    const buildResult = (): SendConfirmResult => ({
        recipients,
        productLabel: productSummary,
        totalAmount,
        payment,
        message,
        reason: payload.reason,
        route: payload.route,
        receiverType: recipients.length > 1 ? '단체' : payload.recipientType,
        directPayMethod: payment === '바로 결제' ? 'CHECKOUT' : undefined,
        idempotencyBaseKey,
    });

    const submit = async () => {
        if (!canSend || isSubmitting) return;
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const outcome = await onConfirm(buildResult());
            if (payment === '충전 잔액') {
                setIsQueuedSend(!!outcome?.queued);
                setIsSent(true);
            }
            // 바로 결제는 onConfirm 안에서 토스 결제창으로 리다이렉트되므로 여기 도달하지 않는 것이 정상.
            // (리다이렉트 전 짧은 순간을 위해 버튼은 계속 잠가둔다)
        } catch (error: any) {
            setSubmitError(error?.message || '발송에 실패했어요. 잠시 후 다시 시도해 주세요.');
            setIsSubmitting(false);
        }
    };

    if (isSent) {
        return (
            <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[rgba(15,23,42,0.38)] px-[24px]">
                <div className="w-full max-w-[420px] rounded-[8px] bg-white p-[28px] text-center shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
                    <div className="mx-auto flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#E5F8F3]">
                        <Check size={26} color="#008C7B" />
                    </div>
                    <h2 className="mt-[14px] text-[18px] font-bold text-[#1F2937]">
                        {isQueuedSend ? '발송을 시작했어요' : '선물을 보냈습니다'}
                    </h2>
                    <p className="mt-[6px] text-[13px] leading-[20px] text-[#64748B]">
                        {recipients.length}명에게 {productSummary}
                        <br />
                        총 {formatAmount(totalAmount)} · {payment}
                        {isQueuedSend && (
                            <>
                                <br />
                                인원이 많아 순차 발급 중이에요 — 발송 내역에서 진행 상황을 확인할 수 있어요.
                            </>
                        )}
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            onSent?.();
                            onClose();
                        }}
                        className="mt-[18px] h-[42px] w-full rounded-[8px] bg-[#3D73DD] text-[13px] font-bold text-white"
                    >
                        발송 내역 보기
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[rgba(15,23,42,0.38)] px-[24px] py-[24px]">
            <div className="flex max-h-[92vh] w-full max-w-[960px] flex-col overflow-hidden rounded-[8px] bg-white shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
                <div className="flex items-start justify-between border-b border-[#E2E8F0] px-[24px] py-[18px]">
                    <div>
                        <h2 className="text-[18px] font-bold leading-[26px] text-[#1F2937]">선물 보내기</h2>
                        <p className="mt-[2px] text-[13px] leading-[20px] text-[#64748B]">
                            받는 사람, 선물과 메시지, 결제 방법을 확인하세요.
                        </p>
                    </div>
                    <button type="button" aria-label="닫기" onClick={onClose} className="rounded-[8px] p-[6px] hover:bg-[#F8FAFC]">
                        <X size={20} color="#64748B" />
                    </button>
                </div>

                <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_320px] overflow-y-auto lg:overflow-hidden">
                    <div className="overflow-y-auto px-[24px] py-[20px]">
                        <section>
                            <div className="flex items-center justify-between gap-[12px]">
                                <h3 className="shrink-0 whitespace-nowrap text-[14px] font-bold text-[#1F2937]">받는 사람 <span className="text-[#3D73DD]">{recipients.length}명</span></h3>
                                <div className="flex flex-wrap justify-end gap-[5px]">
                                    {reasonCounts.map(([reason, count]) => (
                                        <span key={reason} className="rounded-full bg-[#F1F5F9] px-[8px] py-[4px] text-[11px] font-bold text-[#475569]">
                                            {reason} {count}명
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-[10px] max-h-[188px] overflow-y-auto rounded-[8px] border border-[#E2E8F0]">
                                {recipients.map((recipient, index) => (
                                    <div key={`${recipient.name}-${index}`} className="grid min-h-[58px] grid-cols-[130px_1fr_30px] items-center gap-[10px] border-b border-[#F1F5F9] px-[12px] last:border-b-0">
                                        <div className="min-w-0">
                                            <p className="truncate text-[13px] font-bold text-[#1F2937]">{recipient.name}</p>
                                            <p className="mt-[2px] text-[11px] text-[#94A3B8]">{recipient.phone ?? recipient.detail}</p>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="truncate text-[12px] font-bold text-[#475569]">{recipient.reason ?? payload.reason}</p>
                                            {recipient.trainer && <p className="mt-[2px] text-[11px] text-[#94A3B8]">담당 {recipient.trainer} 강사</p>}
                                        </div>
                                        <button
                                            type="button"
                                            aria-label={`${recipient.name} 제외`}
                                            onClick={() => setRecipients((current) => current.filter((_, recipientIndex) => recipientIndex !== index))}
                                            className="flex h-[28px] w-[28px] items-center justify-center rounded-[8px] text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#475569]"
                                        >
                                            <X size={15} />
                                        </button>
                                    </div>
                                ))}
                                {recipients.length === 0 && (
                                    <p className="px-[12px] py-[24px] text-center text-[12px] text-[#94A3B8]">받는 사람이 없습니다.</p>
                                )}
                            </div>
                        </section>

                        <section className="mt-[20px] border-t border-[#E2E8F0] pt-[18px]">
                            <div className="flex items-center justify-between gap-[12px]">
                                <h3 className="text-[14px] font-bold text-[#1F2937]">보낼 선물</h3>
                                {visibleGiftGroups.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => onRequestProductChange()}
                                        className="h-[30px] rounded-[8px] border border-[#CBD5E1] bg-white px-[10px] text-[11px] font-bold text-[#334155] hover:bg-[#F8FAFC]"
                                    >
                                        모두 같은 선물로 변경
                                    </button>
                                )}
                            </div>
                            <div className="mt-[10px] space-y-[8px]">
                                {visibleGiftGroups.map((group) => {
                                    const recipientCount = groupRecipientCount(group.key);
                                    const groupTotal = group.product.price * recipientCount;
                                    return (
                                        <div key={group.key} className="flex items-center gap-[12px] rounded-[8px] bg-[#F8FAFC] p-[12px]">
                                            <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-[8px] bg-[#EAF0FC] text-[#3D73DD]">
                                                <Gift size={24} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[11px] font-bold text-[#3D73DD]">{group.label} · {recipientCount}명</p>
                                                <p className="mt-[2px] truncate text-[13px] font-bold text-[#1F2937]">{group.product.brand} {group.product.name}</p>
                                                <p className="mt-[4px] text-[12px] text-[#475569]">1명당 <b>{point(group.product.price)}</b> · 합계 <b>{point(groupTotal)}</b></p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => onRequestProductChange(group.key)}
                                                className="h-[34px] shrink-0 rounded-[8px] border border-[#CBD5E1] bg-white px-[11px] text-[12px] font-bold text-[#334155] hover:bg-[#F8FAFC]"
                                            >
                                                변경
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        <section className="mt-[20px] border-t border-[#E2E8F0] pt-[18px]">
                            <div className="flex items-center justify-between">
                                <h3 className="text-[14px] font-bold text-[#1F2937]">함께 보낼 메시지</h3>
                                <span className="text-[11px] text-[#94A3B8]">{message.length}자</span>
                            </div>
                            <div className="mt-[9px] flex flex-wrap gap-[6px]">
                                {messageTemplates.map((template) => (
                                    <button
                                        key={template.label}
                                        type="button"
                                        onClick={() => setMessage(template.value)}
                                        className="h-[30px] rounded-full border border-[#CBD5E1] bg-white px-[10px] text-[11px] font-bold text-[#475569] hover:border-[#3D73DD] hover:text-[#3D73DD]"
                                    >
                                        {template.label}
                                    </button>
                                ))}
                            </div>
                            <textarea
                                value={message}
                                onChange={(event) => setMessage(event.target.value)}
                                placeholder="함께 보낼 메시지를 입력하세요."
                                className="mt-[9px] h-[96px] w-full resize-none rounded-[8px] border border-[#CBD5E1] p-[12px] text-[13px] leading-[20px] outline-none focus:border-[#3D73DD]"
                            />
                            <p className="mt-[6px] text-[11px] text-[#94A3B8]">{'{이름}'}은 받는 사람의 이름으로 각각 바뀌어 전달됩니다.</p>
                        </section>
                    </div>

                    <aside className="overflow-y-auto border-l border-[#E2E8F0] bg-[#FBFCFE] px-[18px] py-[20px]">
                        <h3 className="text-[14px] font-bold text-[#1F2937]">결제 방법</h3>
                        <div className="mt-[10px] space-y-[8px]">
                            <div
                                onClick={() => setPayment('충전 잔액')}
                                className={`w-full cursor-pointer rounded-[8px] border p-[12px] text-left ${payment === '충전 잔액' ? 'border-[#3D73DD] bg-[#F4F7FD]' : 'border-[#E2E8F0] bg-white'}`}
                            >
                                <div className="flex items-center gap-[8px]">
                                    <span className={`flex h-[18px] w-[18px] items-center justify-center rounded-full border ${payment === '충전 잔액' ? 'border-[#3D73DD]' : 'border-[#CBD5E1]'}`}>
                                        {payment === '충전 잔액' && <span className="h-[8px] w-[8px] rounded-full bg-[#3D73DD]" />}
                                    </span>
                                    <WalletCards size={17} color="#3D73DD" />
                                    <b className="text-[13px] text-[#1F2937]">선물 포인트 사용</b>
                                    <span className="ml-auto rounded-full bg-[#E5F8F3] px-[7px] py-[3px] text-[10px] font-bold text-[#008C7B]">최대 7% 혜택</span>
                                </div>
                                <div className="mt-[10px] space-y-[5px] pl-[26px] text-[11px] text-[#64748B]">
                                    <div className="flex justify-between"><span>현재 잔액</span><b className="text-[#334155]">{point(pointBalance)}</b></div>
                                    <div className="flex justify-between"><span>이번 발송</span><b className="text-[#334155]">-{point(pointTotal)}</b></div>
                                    <div className="flex justify-between border-t border-[#E2E8F0] pt-[5px]"><span>발송 후 잔액</span><b className={hasEnoughPoints ? 'text-[#3D73DD]' : 'text-[#DC2626]'}>{point(pointBalance - pointTotal)}</b></div>
                                </div>
                                {!hasEnoughPoints && (
                                    <div className="mt-[8px] pl-[26px]">
                                        <p className="text-[11px] font-bold text-[#DC2626]">
                                            {pointBalance <= 0 ? '보유 포인트가 없습니다.' : '포인트가 부족합니다.'}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setPayment('충전 잔액');
                                                onRequestCharge();
                                            }}
                                            className="mt-[6px] h-[30px] rounded-[7px] bg-[#3D73DD] px-[11px] text-[11px] font-bold text-white"
                                        >
                                            포인트 충전하기
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div
                                onClick={() => setPayment('바로 결제')}
                                className={`w-full cursor-pointer rounded-[8px] border p-[12px] text-left ${payment === '바로 결제' ? 'border-[#3D73DD] bg-[#F4F7FD]' : 'border-[#E2E8F0] bg-white'}`}
                            >
                                <div className="flex items-center gap-[8px]">
                                    <span className={`flex h-[18px] w-[18px] items-center justify-center rounded-full border ${payment === '바로 결제' ? 'border-[#3D73DD]' : 'border-[#CBD5E1]'}`}>
                                        {payment === '바로 결제' && <span className="h-[8px] w-[8px] rounded-full bg-[#3D73DD]" />}
                                    </span>
                                    <CreditCard size={17} color="#3D73DD" />
                                    <b className="text-[13px] text-[#1F2937]">바로 결제</b>
                                    <span className="ml-auto rounded-full bg-[#E5F8F3] px-[7px] py-[3px] text-[10px] font-bold text-[#008C7B]">3% 할인</span>
                                </div>
                                <div className="mt-[10px] space-y-[5px] pl-[26px] text-[11px] text-[#64748B]">
                                    <div className="flex justify-between"><span>상품 금액</span><b className="text-[#334155]">{money(pointTotal)}</b></div>
                                    <div className="flex justify-between"><span>바로 결제 할인</span><b className="text-[#008C7B]">-{money(directDiscount)}</b></div>
                                    <div className="flex justify-between border-t border-[#E2E8F0] pt-[5px]"><span>최종 결제</span><b className="text-[#3D73DD]">{money(directTotal)}</b></div>
                                </div>
                                {payment === '바로 결제' && (
                                    <div className="mt-[10px] space-y-[6px] pl-[26px]" onClick={(event) => event.stopPropagation()}>
                                        {checkoutBlockedByMultiGroup ? (
                                            <div className="rounded-[8px] border border-[#FDE68A] bg-[#FFFBEB] px-[10px] py-[8px] text-[11px] leading-[16px] text-[#92400E]">
                                                바로 결제는 한 번에 한 가지 상품만 보낼 수 있어요. 상품을 하나로 맞추거나 충전 잔액으로 보내주세요.
                                            </div>
                                        ) : (
                                            <div className="rounded-[8px] border border-[#E2E8F0] bg-white px-[10px] py-[8px]">
                                                <p className="text-[12px] font-bold text-[#334155]">토스 카드 결제</p>
                                                <p className="text-[11px] text-[#94A3B8]">보내기를 누르면 토스페이먼츠 결제창으로 이동합니다</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-[18px] border-t border-[#E2E8F0] pt-[16px]">
                            <p className="text-[12px] font-bold text-[#64748B]">받는 사람이 보게 될 메시지</p>
                            <div className="mt-[8px] rounded-[8px] border border-[#DCE6F8] bg-white p-[12px]">
                                <div className="flex items-center gap-[9px]">
                                    <div className="flex h-[34px] w-[34px] items-center justify-center rounded-[8px] bg-[#EAF0FC] text-[#3D73DD]">
                                        <Gift size={17} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] text-[#94A3B8]">우리 센터에서 보낸 선물</p>
                                        <p className="truncate text-[12px] font-bold text-[#1F2937]">{previewGroup ? `${previewGroup.product.brand} ${previewGroup.product.name}` : '선물'}</p>
                                    </div>
                                </div>
                                <p className="mt-[10px] whitespace-pre-wrap text-[11px] leading-[17px] text-[#475569]">{previewMessage || '메시지를 입력하세요.'}</p>
                            </div>
                        </div>
                    </aside>
                </div>

                <div className="border-t border-[#E2E8F0] bg-white px-[24px] py-[14px]">
                    {submitError && (
                        <p className="mb-[8px] rounded-[8px] border border-[#FECACA] bg-[#FEF2F2] px-[10px] py-[7px] text-[12px] leading-[17px] text-[#B91C1C]">
                            {submitError}
                        </p>
                    )}
                    <div className="flex items-center justify-between gap-[12px]">
                        <div>
                            <p className="text-[11px] text-[#64748B]">총 발송 금액</p>
                            <p className="mt-[1px] text-[18px] font-bold text-[#3D73DD]">{formatAmount(totalAmount)}</p>
                        </div>
                        <div className="flex gap-[8px]">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="h-[42px] rounded-[8px] border border-[#CBD5E1] px-[16px] text-[13px] font-bold text-[#334155] disabled:opacity-50"
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                disabled={!canSend || isSubmitting}
                                onClick={submit}
                                className="h-[42px] min-w-[210px] rounded-[8px] bg-[#3D73DD] px-[18px] text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:bg-[#CBD5E1]"
                            >
                                {isSubmitting
                                    ? payment === '바로 결제' ? '결제창으로 이동 중…' : '보내는 중…'
                                    : payment === '바로 결제'
                                        ? `결제하고 보내기 · ${formatAmount(totalAmount)}`
                                        : `${recipients.length}명에게 보내기 · ${formatAmount(totalAmount)}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendConfirmModal;
